/**
 * @fileoverview Global state management implementation using React Context API
 * 
 * This file implements a global state store for the application using React's
 * Context API and useReducer hook. It provides centralized state management
 * that can be accessed throughout the component tree without prop drilling.
 * 
 * The store manages critical application data including:
 * - Leader profile information
 * - Client configuration data
 * - Lookup reference data
 * - UI state like logout modal visibility
 */

import React, { createContext, ReactElement, ReactNode, useMemo, useReducer } from 'react';
import Reducer from './reducer';
import { ContextType } from './types';
/**
 * Main Context Provider component that wraps the application
 * 
 * This component initializes the global state using useReducer and wraps
 * the application with the context provider, making state and dispatch
 * functions available to all child components.
 * 
 * @param {Object} props - Component properties
 * @param {ReactNode} props.children - Child components to be wrapped by the provider
 * @returns {ReactElement} Context provider wrapping children components
 */
export function GlobalStore({ children }: { children: ReactNode }): ReactElement {
  // Initialize the global state with the reducer and initial state
  const [globalState, dispatch] = useReducer(Reducer, initializeState());

  // Memoize the context value to prevent unnecessary re-renders
  const globalContextValue = useMemo(
    () => ({
      dispatch, globalState
    }),
    [dispatch, globalState]
  );

  return <globalContext.Provider value={globalContextValue}>{children}</globalContext.Provider>;
}

/**
 * Global context instance for the application state
 * 
 * Components can consume this context using the useContext hook:
 * const { globalState, dispatch } = useContext(globalContext);
 * 
 * @type {React.Context<ContextType>}
 */
export const globalContext = createContext({} as ContextType);

/**
 * Initial state definition for the global store
 * 
 * This object defines the default values for all properties in the global state.
 * Any changes to the state structure should be reflected here first.
 * 
 * @property {Object} leaderData - Leader profile information
 * @property {Object} clientData - Client configuration information
 * @property {Array} lookUpData - Reference data for lookups and dropdowns
 * @property {boolean} showLogoutModal - Controls visibility of the logout confirmation modal
 */
export const initialState: any = {
  leaderData: {},     // Leader profile data including assessment history
  clientData: {},     // Client-specific configuration and branding
  lookUpData: [],     // System lookup data for dropdowns and references
  showLogoutModal: false, // Flag to control logout confirmation modal visibility
  leftPanelImg: '',
  headerLogo: '',
  headerImg : '',
  immersiveAssessmentData: '',
  demographicAcknowledged: false, // Flag to track if user acknowledged demographic usage modal
};

/**
 * Helper function to initialize the global state
 * 
 * Currently returns the initialState object directly, but this function
 * can be expanded to handle more complex initialization logic such as:
 * - Loading state from localStorage/sessionStorage
 * - Merging default values with persisted state
 * - Initializing derived state values
 * 
 * @returns {Object} The initial state for the global store
 */
function initializeState() {
  return initialState;
}
